import { useMemo } from "react";
import { Alert, Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { useServerVersion } from "../hooks/useServerVersion";
import impleoLogoUrl from "../assets/Impleo_logo.png";

const AboutForm = () => {
  const {
    versionLabel,
    updateInfo,
    refreshReleaseInfo,
    releaseLoading,
  } = useServerVersion();

  const publishedDisplay = useMemo(() => {
    if (!updateInfo.publishedAt) {
      return "Unavailable";
    }
    const date = new Date(updateInfo.publishedAt);
    if (Number.isNaN(date.getTime())) {
      return updateInfo.publishedAt;
    }
    return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  }, [updateInfo.publishedAt]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.5,
        textAlign: "center",
        mt: 1,
      }}
    >
      <Box
        component="img"
        src={impleoLogoUrl}
        alt="ImpleoTV Logo"
        sx={{ height: 36, mb: 0.5 }}
      />
      {versionLabel ? (
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {versionLabel}
        </Typography>
      ) : (
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Version information unavailable
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {`Published on ${publishedDisplay}`}
      </Typography>
      {updateInfo.isLatest && (
        <Typography variant="body2" sx={{ color: "success.main", fontWeight: 600 }}>
          Latest version installed
        </Typography>
      )}
      {!updateInfo.isLatest && updateInfo.hasUpdate && updateInfo.latestVersion && (
        <Typography variant="body2" sx={{ color: "info.main", fontWeight: 600 }}>
          {`Update available: ${updateInfo.latestVersion}`}
        </Typography>
      )}
      {!updateInfo.hasUpdate && updateInfo.error && (
        <Alert severity="warning" sx={{ width: "100%" }}>
          {updateInfo.error}
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Check for update">
          <span>
            <IconButton
              size="small"
              onClick={refreshReleaseInfo}
              disabled={releaseLoading}
              aria-label="Check for update"
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {releaseLoading && <CircularProgress size={16} thickness={6} />}
      </Box>
    </Box>
  );
};

export default AboutForm;
